import { useEffect, useMemo, useState, useCallback } from "react";
import { getDataWAuth, postDataWAuth } from "../../utils/api-service";
import {
  REPORT_CUSTOMER_INTEREST_DRILLDOWN_YEAR, COUNTRY_CODE_MAP, REPORT_CUSTOMER_INTEREST_DOMAIN_DATA,
} from "../../constants/constant";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import { Button, Card, CardContent, Option, Select } from "@deere/fuel-react";

import { FileDownloadOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Popover } from "@mui/material";
import * as XLSX from "xlsx";


/* ── helpers ─────────────────────────────────────────── */
const fmt = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? "" : n.toFixed(2);
};


const buildTreeData = (raw) => {
  const monthSet = new Set();
  const monthInfo = {};

  // Aggregation maps
  const custAgg = {};       // custNum → { month → interest }
  const custNames = {};     // custNum → customerName
  const asnAgg = {};        // custNum → asnNum → { month → interest }
  const asnSerials = {};    // custNum → asnNum → Set of serial numbers
  const asnBlanks = {};     // custNum → asnNum → total absolute blank interest
  const asnCurrency = {};   // custNum → asnNum → currency

  (raw || []).forEach((entry) => {
    const month = entry.month;
    monthSet.add(month);
    // capture start/end date strings for month header formatting
    monthInfo[month] = {
      startDate: entry.startDate,
      endDate: entry.endDate,
    };

    (entry.customers || []).forEach((cust) => {
      const cNum = String(cust.customerNumber);
      const cName = cust.customerName || "";
      custNames[cNum] = cName;
      if (!custAgg[cNum]) custAgg[cNum] = {};
      if (!asnAgg[cNum]) asnAgg[cNum] = {};
      if (!asnSerials[cNum]) asnSerials[cNum] = {};
      if (!asnBlanks[cNum]) asnBlanks[cNum] = {};
      if (!asnCurrency[cNum]) asnCurrency[cNum] = {};

      let custTotal = 0;

      (cust.assignments || []).forEach((asn) => {
        const aNum = String(asn.assignmentNumber);
        const interest = Number(asn.totalInterest || 0);
        custTotal += interest;

        if (!asnAgg[cNum][aNum]) asnAgg[cNum][aNum] = {};
        asnAgg[cNum][aNum][month] = (asnAgg[cNum][aNum][month] || 0) + interest;

        if (!asnSerials[cNum][aNum]) asnSerials[cNum][aNum] = new Set();
        if (!asnBlanks[cNum][aNum]) asnBlanks[cNum][aNum] = 0;

        // Store currency from assignment
        if (!asnCurrency[cNum][aNum] && asn.currency) {
          asnCurrency[cNum][aNum] = asn.currency;
        }

        const withSerial = asn.lineItems?.withSerialNumber || [];
        const withoutSerial = asn.lineItems?.withoutSerialNumber || [];

        withSerial.forEach((si) => {
          if (si.serialNumber) {
            asnSerials[cNum][aNum].add(si.serialNumber);
          }
        });

        withoutSerial.forEach((si) => {
          asnBlanks[cNum][aNum] += Math.abs(Number(si.interest || 0));
        });
      });

      custAgg[cNum][month] = (custAgg[cNum][month] || 0) + custTotal;
    });
  });

  const months = Array.from(monthSet).sort((a, b) => {
    const [mA, yA] = a.split("-").map(Number);
    const [mB, yB] = b.split("-").map(Number);
    return yA !== yB ? yA - yB : mA - mB;
  });
  const rows = [];
  let id = 0;

  // Build readable month labels like "29 Dec 2025 to 01 Feb 2026" using dayjs
  const monthLabels = {};
  months.forEach((m) => {
    const info = monthInfo[m] || {};
    const start = info.startDate;
    const end = info.endDate;
    if (start && end) {
      try {
        const sStr = dayjs(start).format("DD MMM YYYY");
        const eStr = dayjs(end).format("DD MMM YYYY");
        monthLabels[m] = `${sStr} to ${eStr}`;
      } catch (err) {
        monthLabels[m] = m;
      }
    } else {
      monthLabels[m] = m;
    }
  });

  // Build flat rows with 2-level hierarchy (Customer → Assignment)
  Object.keys(custAgg)
    .sort()
    .forEach((cNum) => {
      // Assignment rows (serial numbers & blanks as columns)
      const assignments = asnAgg[cNum] || {};
      const validAssignments = [];

      Object.keys(assignments)
        .sort()
        .forEach((aNum) => {
          // Check if all monthly interest rates are zero
          const allMonthsZero = months.every((m) => {
            const value = assignments[aNum][m] ?? 0;
            return value === 0 || value === null;
          });

          // Skip this assignment if all months have zero interest
          if (allMonthsZero) {
            return;
          }

          validAssignments.push(aNum);

          const serials = asnSerials[cNum]?.[aNum] || new Set();
          const blanksTotal = asnBlanks[cNum]?.[aNum] || 0;

          const asnRow = {
            id: id++,
            label: aNum,
            path: [cNum, aNum],
            customerName: "",
            serialNumber: Array.from(serials).join(", "),
            blanks: blanksTotal > 0 ? blanksTotal : null,
            currency: asnCurrency[cNum]?.[aNum] || "-",
          };
          months.forEach((m) => {
            asnRow[m] = assignments[aNum][m] ?? null;
          });
          rows.push(asnRow);
        });

      // Only add customer row if it has at least one valid (non-zero) assignment
      if (validAssignments.length > 0) {
        const custRow = {
          id: id++,
          label: cNum,
          path: [cNum],
          customerName: custNames[cNum] || "",
          serialNumber: "",
          blanks: null,
        };
        months.forEach((m) => {
          custRow[m] = custAgg[cNum][m] ?? null;
        });
        // Insert customer row at the beginning of this customer's section
        const customerRowIndex = rows.length - validAssignments.length;
        rows.splice(customerRowIndex, 0, custRow);
      }
    });

  return { months, rows, monthLabels };
};

/* ── Toolbar (matches Dealer Rate table) ─────────────── */
const GridToolbar = ({ onExport }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <Button
      color="inherit"
      size="small"
      startIcon={<FileDownloadOutlined style={{ color: "green" }} />}
      style={{
        boxShadow: "none",
        border: "none",
        background: "none",
        color: "green",
      }}
      onClick={onExport}
    >
      Export
    </Button>
  </GridToolbarContainer>
);

/* ── Custom Multi-Select Component ──────── */
const CustomerMultiSelect = ({ options, selected, onSelectionChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleToggle = (option) => {
    const isSelected = selected.includes(option);
    const newSelected = isSelected
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onSelectionChange(newSelected);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        fullWidth
        value={selected.length === 0 ? "0 customers selected" : `${selected.length} customer${selected.length !== 1 ? 's' : ''} selected`}
        onClick={handleOpenMenu}
        InputProps={{
          readOnly: true,
          style: { cursor: 'pointer' }
        }}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            cursor: 'pointer'
          }
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 300, maxHeight: 400, overflow: 'auto' }}>
          <List sx={{ p: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  const allSelected = selected.length === options.length;
                  onSelectionChange(allSelected ? [] : options);
                }}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  },
                  py: 0.5,
                  px: 1,
                  borderBottom: '1px solid #eee'
                }}
              >
                <ListItemIcon sx={{ minWidth: '36px' }}>
                  <Checkbox
                    edge="start"
                    checked={selected.length === options.length && options.length > 0}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={selected.length === options.length && options.length > 0 ? "Unselect All" : "Select All"}
                  primaryTypographyProps={{ fontSize: '13px', fontWeight: '500', color: '#000000' }}
                />
              </ListItemButton>
            </ListItem>
            {options.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <ListItem key={option} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggle(option)}
                    sx={{
                      backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                      },
                      py: 0.5,
                      px: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={option}
                      primaryTypographyProps={{ fontSize: '13px' }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
};

/* ── Main component ──────────────────────────────────── */
const CustomerInterestDrilldownReport = () => {
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Filter state
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [domainDataUnavailable, setDomainDataUnavailable] = useState(false);
  const [countryCustomersMap, setCountryCustomersMap] = useState({});

  // Load filter options AND initial report data on mount (single API call)
  useEffect(() => {
    const loadFiltersAndData = async () => {
      try {
        setFiltersLoading(true);

        // Single API call that returns both filter data and report data
        const resp = await getDataWAuth(REPORT_CUSTOMER_INTEREST_DOMAIN_DATA);

        if (resp?.status === 200) {
          const responseData = resp.data || {};

          // Extract filter data from interestDominData
          const domainData = responseData.interestDominData || {};
          const countryCodes = Array.isArray(domainData.countryList) ? domainData.countryList : [];
          const countryOpts = countryCodes.map((code) => ({ countryCode: code, countryName: COUNTRY_CODE_MAP[code] || code }));
          setCountryOptions(countryOpts);

          const fiscalYears = Array.isArray(domainData.fiscalYearDTOList) ? domainData.fiscalYearDTOList.map((f) => f.fiscalYear) : [];
          const yearsToUse = fiscalYears.length > 0 ? fiscalYears.slice().sort() : [String(new Date().getFullYear())];
          setYearOptions(yearsToUse);

          const defaultCountryCode = domainData.defaultCountry?.countryCode || domainData.defaultCountry?.country || (countryCodes[0] || Object.keys(COUNTRY_CODE_MAP)[0] || "");
          setSelectedCountry(defaultCountryCode);
          setSelectedCountryName(COUNTRY_CODE_MAP[defaultCountryCode] || defaultCountryCode);

          // Store the country-to-customers map for dynamic updates
          const custMap = responseData.countryCustomersMap || {};
          setCountryCustomersMap(custMap);

          // Get customers for the default country from countryCustomersMap
          const customersForCountry = custMap[defaultCountryCode] || [];
          setCustomerOptions(customersForCountry);
          setSelectedCustomers(customersForCountry.slice());

          const defaultFiscal = domainData.defaultFiscalYear?.fiscalYear || (yearsToUse.length > 0 ? yearsToUse[yearsToUse.length - 1] : String(new Date().getFullYear()));
          setSelectedYear(defaultFiscal);

          // Extract report data from fiscalMonths
          const fiscalMonths = Array.isArray(responseData.fiscalMonths) ? responseData.fiscalMonths : [];
          setReportData(fiscalMonths);
          setIsFilterDirty(false);          
          setFiltersLoading(false);
          return;
        }

        // non-200: mark domain data unavailable and stop
        setDomainDataUnavailable(true);
        setFiltersLoading(false);
      } catch (error) {
        setDomainDataUnavailable(true);
        setFiltersLoading(false);
      }
    };
    loadFiltersAndData();
  }, []);


  const handleCountryChange = (e) => {
    const code = e.target.value;
    setSelectedCountry(code);
    setSelectedCountryName(COUNTRY_CODE_MAP[code] || code);

    // Update customer list based on selected country
    const customersForCountry = countryCustomersMap[code] || [];
    setCustomerOptions(customersForCountry);
    // Auto-select all customers for the new country
    setSelectedCustomers(customersForCountry.slice());

    setIsFilterDirty(true);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setIsFilterDirty(true);
  };

  const handleViewReport = useCallback(async () => {
    if (!selectedCountry || !selectedYear) {
      return;
    }
    try {
      setIsLoading(true);
      const requestBody = {
        year: selectedYear,
        country: selectedCountry,
        customerParams: selectedCustomers
      };
      const reportResp = await postDataWAuth(REPORT_CUSTOMER_INTEREST_DRILLDOWN_YEAR, requestBody);

      // Handle response - could be array (old format) or object with fiscalMonths (new format)
      let reportRows = [];
      if (reportResp?.data) {
        if (Array.isArray(reportResp.data)) {
          // Old format: direct array
          reportRows = reportResp.data;
        } else if (reportResp.data.fiscalMonths) {
          // New format: contains fiscalMonths array
          reportRows = reportResp.data.fiscalMonths;
        }
      }

      setReportData(reportRows);
      setIsFilterDirty(false);
    } catch (err) {
      console.error("Error fetching report:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCountry, selectedYear, selectedCustomers]);

  const { months, rows, monthLabels } = useMemo(() => buildTreeData(reportData), [reportData]);

  const getTreeDataPath = useCallback((row) => row.path, []);

  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "Serial Number",
        width: 200,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 100,
        headerAlign: "left",
        align: "left",
      },
      ...months.map((m, index) => ({
        field: m,
        headerName: monthLabels?.[m] || m,
        width: 150,
        headerAlign: "right",
        align: "right",
        renderHeader: (params) => {
          const header = params.colDef.headerName;
          const formattedHeader = header.replace(" to ", " to\n");

          return (
            <div
              style={{
                whiteSpace: "pre-line",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {formattedHeader} {" "}
            </div>
          );
        },
        valueFormatter: (params) => (params != null ? fmt(params) : "-"),
      })),
    ],
    [months, monthLabels],
  );

  const groupingColDef = useMemo(
    () => ({
      headerName: "Customer Number",
      width: 280,
      headerAlign: "left",
      hideDescendantCount: true,
      sortable: true,
      filterable: true,
      pinnable: true,
      hideable: true,
    }),
    [],
  );

  const handleExportXLSX = useCallback(() => {
    // Build export data from reportData with proper structure
    const monthSet = new Set();
    const monthInfo = {};
    const customerData = {}; // customerNum -> { name, assignments: [...] }

    // First pass: collect all months and build customer structure
    (reportData || []).forEach((entry) => {
      const month = entry.month;
      monthSet.add(month);
      monthInfo[month] = {
        startDate: entry.startDate,
        endDate: entry.endDate,
      };

      (entry.customers || []).forEach((cust) => {
        const custNum = String(cust.customerNumber);
        const custName = cust.customerName || "";

        if (!customerData[custNum]) {
          customerData[custNum] = {
            name: custName,
            assignments: {},
          };
        }

        (cust.assignments || []).forEach((asn) => {
          const asnNum = String(asn.assignmentNumber);
          const currency = asn.currency || "-";
          const interest = Number(asn.totalInterest || 0);

          if (!customerData[custNum].assignments[asnNum]) {
            customerData[custNum].assignments[asnNum] = {
              currency: currency,
              serials: new Set(),
              monthData: {},
              // Check if all monthly interest rates are zero
              hasNonZeroInterest: false,
            };
          }

          customerData[custNum].assignments[asnNum].monthData[month] = interest;
          // Check if all monthly interest rates are zero
          if (interest !== 0) {
            customerData[custNum].assignments[asnNum].hasNonZeroInterest = true;
          }

          // Collect serial numbers
          const withSerial = asn.lineItems?.withSerialNumber || [];
          withSerial.forEach((si) => {
            if (si.serialNumber) {
              customerData[custNum].assignments[asnNum].serials.add(si.serialNumber);
            }
          });
        });
      });
    });

    // Sort months
    const months = Array.from(monthSet).sort((a, b) => {
      const [mA, yA] = a.split("-").map(Number);
      const [mB, yB] = b.split("-").map(Number);
      return yA !== yB ? yA - yB : mA - mB;
    });

    // Build month labels
    const monthLabels = {};
    months.forEach((m) => {
      const info = monthInfo[m] || {};
      const start = info.startDate;
      const end = info.endDate;
      if (start && end) {
        try {
          const sStr = dayjs(start).format("DD MMM YYYY");
          const eStr = dayjs(end).format("DD MMM YYYY");
          monthLabels[m] = `${sStr} to ${eStr}`;
        } catch (err) {
          monthLabels[m] = m;
        }
      } else {
        monthLabels[m] = m;
      }
    });

    // Build export rows
    const exportRows = [];
    const mergeInfo = []; // Track which rows to merge

    let rowIndex = 0;
    Object.keys(customerData)
      .sort()
      .forEach((custNum) => {
        const custInfo = customerData[custNum];
        const assignments = Object.keys(custInfo.assignments).sort();
        const mergeStartRow = rowIndex;
        let validAssignmentCount = 0;

        assignments.forEach((asnNum) => {
          const asnInfo = custInfo.assignments[asnNum];
          // Check if all monthly interest rates are zero
          if (!asnInfo.hasNonZeroInterest) {
            return;
          }

          const serials = Array.from(asnInfo.serials).join(", ");

          const row = {
            "Customer Number - Name": custNum, // Always show full customer identifier
            "Assignment Number": asnNum,
            "Serial Number": serials,
            "Currency": asnInfo.currency,
          };

          months.forEach((m) => {
            const val = asnInfo.monthData[m];
            row[monthLabels?.[m] || m] = val != null ? Number(val).toFixed(2).replace(".", ",") : "";
          });

          exportRows.push(row);
          validAssignmentCount++;
          rowIndex++;
        });

        // Store merge range for this customer's rows only if there are valid assignments
        if (validAssignmentCount > 0) {
          mergeInfo.push({
            start: mergeStartRow,
            end: rowIndex - 1,
          });
        }
      });

    const ws = XLSX.utils.json_to_sheet(exportRows);

    // Set column widths
    ws["!cols"] = [
      { wch: 35 }, // Customer Number - Name
      { wch: 18 }, // Assignment Number
      { wch: 25 }, // Serial Number
      { wch: 12 }, // Currency
      ...months.map(() => ({ wch: 18 })), // Month columns
    ];

    // Apply merges for customer identifier column (Column A)
    const merges = [];
    mergeInfo.forEach((info) => {
      if (info.start !== info.end) {
        // Only merge if multiple rows - adjust row indices by +1 for header row
        merges.push({
          s: { r: info.start + 1, c: 0 }, // Start at column A, adjust for header
          e: { r: info.end + 1, c: 0 }, // End at last assignment row, adjust for header
        });
      }

      // Apply center alignment to merged cells
      for (let row = info.start + 1; row <= info.end + 1; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (ws[cellRef]) {
          ws[cellRef].alignment = {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          };
        }
      }
    });

    if (merges.length > 0) {
      ws["!merges"] = merges;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Monthly Booked Interest");
    XLSX.writeFile(wb, `Monthly Booked Interest.xlsx`);
  }, [reportData, selectedYear]);

  /* ── render ──────────────────────────────────────── */
  if (filtersLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 1 }}>Loading filters…</Typography>
      </Box>
    );
  }

  if (domainDataUnavailable) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  return (
    <div style={{ paddingTop: "20px", height: "100%", width: "100%" }}>
      {/* ── Filters ── */}
      <Box>
        <Card>
          <CardContent>
            <h3 style={{ marginBottom: "1%" }}>Booked Interest Per Dealer by Month</h3>
            <div className="action-area">
              <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
                <label>Country</label>
                <Select onChange={handleCountryChange} value={selectedCountry}>
                  {countryOptions && countryOptions.length > 0 ? (
                    countryOptions.map((c) => (
                      <Option key={c.countryCode} value={c.countryCode}>
                        {c.countryName}
                      </Option>
                    ))
                  ) : (
                    selectedCountry ? (
                      <Option key={selectedCountry} value={selectedCountry}>
                        {selectedCountryName || selectedCountry}
                      </Option>
                    ) : null
                  )}
                </Select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
                <label>Customer Number</label>
                <CustomerMultiSelect
                  options={customerOptions}
                  selected={selectedCustomers}
                  onSelectionChange={(newSelected) => {
                    setSelectedCustomers(newSelected);
                    setIsFilterDirty(true);
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", width: "20rem" }}>
                <label>Fiscal Year</label>
                <Select onChange={handleYearChange} value={selectedYear}>
                  {yearOptions.map((y) => (
                    <Option key={y} value={y}>
                      {y}
                    </Option>
                  ))}
                </Select>
              </div>



              <Button
                variant="primary"
                disabled={!isFilterDirty}
                style={{ backgroundColor: isFilterDirty ? "#ffde00" : "gray" }}
                onClick={handleViewReport}
              >
                View Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </Box>

      {/* ── Data Grid ── */}
      <div
        style={{
          marginTop: "25px",
          borderRadius: "10px",
          backgroundColor: "white",
          height: "88%",
        }}
      >
        <DataGridPro
          treeData
          loading={isLoading}
          autoHeight
          rows={rows}
          columns={columns}
          getTreeDataPath={getTreeDataPath}
          groupingColDef={groupingColDef}
          defaultGroupingExpansionDepth={0}
          disableChildrenFiltering
          getRowClassName={(params) => {
            const depth = params.row.path?.length || 0;
            if (depth === 1) return "row-customer";
            return "row-assignment";
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "__tree_data_group__", sort: "asc" }],
            },
          }}
          slots={{
            toolbar: () => <GridToolbar onExport={handleExportXLSX} />,
          }}
          sx={{
            "& .MuiDataGrid-filler": {
              backgroundColor: "white",
            },
            "& .MuiDataGrid-columnHeader--sortable": {
              backgroundColor: "#fff",
            },
            "& .row-customer .MuiDataGrid-cell": {
              fontWeight: 400,
            },
            "& .row-customer .MuiDataGrid-treeDataGroupingCell": {
              fontWeight: 400,
            },
          }}
        />
      </div>
    </div>
  );
};

export default CustomerInterestDrilldownReport;
